// O PDF dos exames de imagem — o mesmo desenho do relatório de acompanhamento.
//
// Reaproveita cores, títulos numerados e o carregador do pdfmake de
// `documentoPdf.ts`, para o documento que a família entrega ao médico ser
// reconhecível como irmão do outro. O que é próprio daqui é a tabela de achado
// por data, que é a razão de este documento existir.
import { formatarData, formatarDataHora, formatarDiaMes } from '@medicov1/shared';
import {
  TINTA, MEIO, FRACO, LINHA, ALTO, OK, FUNDO_CAIXA,
  titulo, comTitulo, baixarPdfDe,
} from './documentoPdf';
import { LEGENDA_DAS_MARCAS, type DocumentoDeImagemDTO } from './RelatorioImagem';
import { identificar } from './identificacao';

/** Margens do pdfmake: [esquerda, cima, direita, baixo]. Nomeadas para não trocar a ordem. */
type Margem = [number, number, number, number];
const margem = (cima: number, direita: number, baixo: number, esquerda: number): Margem =>
  [esquerda, cima, direita, baixo];

/** Monta a definição do pdfmake. Pura: testável fora do navegador. */
export function montarDocumentoDeImagem(dados: DocumentoDeImagemDTO): unknown {
  const quem = identificar(dados.paciente, true);
  const g = dados.gerado;
  const conteudo: unknown[] = [];
  let n = 0;

  // ------------------------------------------------------ identificação
  conteudo.push({
    columns: [
      {
        width: '*',
        stack: [
          { text: quem.nome, fontSize: 19, color: TINTA, margin: margem(0, 0, 1, 0) },
          { text: quem.linha, fontSize: 7.8, color: FRACO },
        ],
      },
      {
        width: 'auto',
        stack: [
          { text: 'EXAMES DE IMAGEM', fontSize: 9, bold: true, characterSpacing: 1.4, alignment: 'right', color: TINTA },
          {
            text: `${g.deExames} laudos${g.primeiro && g.ultimo ? `, de ${formatarDiaMes(g.primeiro)} a ${formatarDiaMes(g.ultimo)}` : ''}`,
            fontSize: 7.5, color: FRACO, alignment: 'right', margin: margem(2, 0, 0, 0),
          },
          { text: `gerado em ${formatarDataHora(g.em)}`, fontSize: 7.5, color: FRACO, alignment: 'right' },
        ],
      },
    ],
    margin: margem(0, 0, 8, 0),
  });
  conteudo.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1.2, lineColor: TINTA }], margin: margem(0, 0, 10, 0) });

  conteudo.push({
    text: [
      'Este documento reúne os laudos de imagem em ordem de data e explica o que cada achado é, com base na literatura citada no fim. ',
      { text: 'Não é diagnóstico', bold: true },
      ': o que os achados significam juntos, e o que fazer com eles, é da equipe.',
    ],
    fontSize: 8.4, color: MEIO, lineHeight: 1.35, margin: margem(0, 0, 6, 0),
  });

  // ------------------------------------------------------------ regiões
  for (const r of dados.regioes) {
    const blocos: unknown[] = [];

    // A tabela: achado por data. As marcas são palavras, não símbolos, porque
    // a fonte do PDF não tem todos os glifos e "●" já virou "I" uma vez aqui.
    if (r.linhas.length > 0) {
      const larguraData = 40;
      blocos.push({
        margin: margem(4, 0, 6, 0),
        table: {
          headerRows: 1,
          widths: ['*', ...r.exames.map(() => larguraData)],
          body: [
            [
              { text: 'ACHADO', fontSize: 6.4, bold: true, color: FRACO, characterSpacing: 0.6 },
              ...r.exames.map((e) => ({
                text: formatarDiaMes(e.em), fontSize: 7, bold: true, color: TINTA, alignment: 'center',
              })),
            ],
            ...r.linhas.map((l) => [
              { text: l.rotulo, fontSize: 8, color: TINTA },
              ...l.marcas.map((m) => ({
                text: m === 'sim' ? 'SIM' : m === 'nao' ? 'NÃO' : '–',
                fontSize: 7, bold: m !== 'nada', alignment: 'center',
                color: m === 'sim' ? ALTO : m === 'nao' ? OK : LINHA,
              })),
            ]),
          ],
        },
        layout: {
          hLineWidth: (i: number) => (i === 1 ? 0.8 : 0.4),
          vLineWidth: () => 0,
          hLineColor: (i: number) => (i === 1 ? TINTA : LINHA),
          paddingLeft: () => 0, paddingRight: () => 4, paddingTop: () => 4, paddingBottom: () => 4,
        },
      });
    }

    // A leitura: o que a linha do tempo dela mostra, com a literatura.
    for (const l of r.leitura) {
      blocos.push({
        margin: margem(2, 0, 5, 0),
        table: {
          widths: [3, '*'],
          body: [[
            { text: '', fillColor: OK },
            {
              stack: [
                { text: l.titulo, fontSize: 8.6, bold: true, color: TINTA },
                { text: l.texto, fontSize: 8, color: MEIO, lineHeight: 1.35, margin: margem(2, 0, 0, 0) },
              ],
              margin: margem(0, 0, 0, 8),
            },
          ]],
        },
        layout: 'noBorders',
      });
    }

    // Os laudos inteiros, em corpo menor: quem quiser conferir a fonte pode.
    for (const e of r.exames) {
      blocos.push({
        margin: margem(4, 0, 4, 0),
        stack: [
          { text: `${e.nome} — ${formatarData(e.em)}`, fontSize: 7.6, bold: true, color: TINTA },
          {
            text: e.laudo, fontSize: 6.9, color: MEIO, lineHeight: 1.3,
            fillColor: FUNDO_CAIXA, margin: margem(2, 0, 0, 0),
          },
        ],
      });
    }

    conteudo.push(...comTitulo(
      titulo(++n, r.rotulo, `${r.exames.length} ${r.exames.length === 1 ? 'exame' : 'exames'}`),
      blocos[0],
      blocos.slice(1),
    ));
  }

  conteudo.push({
    text: `SIM: ${LEGENDA_DAS_MARCAS.sim} · NÃO: ${LEGENDA_DAS_MARCAS.nao} · –: ${LEGENDA_DAS_MARCAS.nada}. `
      + 'Silêncio não é ausência: um laudo que não fala de um achado não está dizendo que ele não existe.',
    fontSize: 6.8, color: FRACO, lineHeight: 1.35, margin: margem(4, 0, 6, 0),
  });

  // ---------------------------------------------------------- glossário
  if (dados.glossario.length > 0) {
    conteudo.push(...comTitulo(
      titulo(++n, 'O que cada achado é'),
      {
        margin: margem(4, 0, 0, 0),
        columns: [0, 1].map((coluna) => ({
          width: '*',
          stack: dados.glossario
            .filter((_, i) => i % 2 === coluna)
            .map((v) => ({
              margin: margem(0, coluna === 0 ? 8 : 0, 6, 0),
              stack: [
                { text: v.rotulo, fontSize: 7.8, bold: true, color: TINTA },
                { text: `${v.oQueE}${v.porQueImporta ? ` ${v.porQueImporta}` : ''}`, fontSize: 7.2, color: MEIO, lineHeight: 1.3 },
              ],
            })),
        })),
      },
    ));
  }

  // ------------------------------------------------------------- fontes
  if (dados.fontes.length > 0) {
    conteudo.push(...comTitulo(
      titulo(++n, 'Fontes'),
      {
        margin: margem(4, 0, 0, 0),
        ol: dados.fontes.map((f) => ({
          stack: [
            { text: f.titulo, fontSize: 7.4, color: TINTA },
            { text: f.url, fontSize: 6.2, color: FRACO, link: f.url },
          ],
          margin: margem(0, 0, 3, 0),
        })),
        fontSize: 7.4,
      },
    ));
  }

  return {
    pageSize: 'A4',
    pageMargins: [40, 46, 40, 44],
    defaultStyle: { font: 'Roboto', fontSize: 8.5, color: TINTA },
    info: { title: 'Exames de imagem — linha do tempo', author: 'saudev1' },
    footer: (pagina: number, total: number) => ({
      columns: [
        { text: 'saudev1 · registro de apoio da família · não é o prontuário do hospital', fontSize: 6.4, color: FRACO },
        { text: `${pagina} / ${total}`, fontSize: 6.4, color: FRACO, alignment: 'right' },
      ],
      margin: margem(0, 40, 0, 40),
    }),
    content: conteudo,
  };
}

export async function gerarPdfImagem(dados: DocumentoDeImagemDTO): Promise<void> {
  const limpo = dados.paciente.apelido.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const data = formatarData(new Date(dados.gerado.em)).replace(/\//g, '-');
  await baixarPdfDe(montarDocumentoDeImagem(dados), `exames-de-imagem-${limpo}-${data}.pdf`);
}
